import {Fragment, useContext, useEffect, useState} from 'react';
import {Typography, ListItemText, List, ListItem, Button, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { UserContext } from '../contexts/UserContext';
import c4ulogo from "../assets/logo.png";
import Footer from './Layout/Footer';
import {jwtDecode} from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import useLog from '../hooks/useLog';
import useCMS from '../hooks/useCMS';


const Login = () => {
    const { t } = useTranslation();
    const { login, loginWithPhone, loggedInState } = useContext(UserContext);
    const { GetSigninCode, ValidateSigninCode } = useCMS();
    const navigate = useNavigate();
    const { LogInfo } = useLog();
    const [loginError, setLoginError] = useState(false);
    const [showPhoneLogin, setShowPhoneLogin] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [communicationMethod, setCommunicationMethod] = useState('sms');
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [phoneLoading, setPhoneLoading] = useState(false);

    useEffect(() => {
        async function checkState() {
            LogInfo(`Login page loaded, logged in state: ${loggedInState}`);
            switch (loggedInState) {
                case 'Unauthenticated':
                    navigate('/login');
                    break;
                case 'CMSAuthenticated':
                    setLoginError(false);
                    navigate('/');
                    break;
                default:
                    break;
            }
        }
        checkState();
    }, [loggedInState]);

    const handleLogin = async (credentialResponse) => {
        setLoginError(false);
        LogInfo("Google login successful, JWT token: " + credentialResponse.credential);
        const details = jwtDecode(credentialResponse.credential);
        const loginResult = await login(details);
        console.log("Login result: " + loginResult);
        setLoginError(!loginResult);
    };

    const handleTogglePhoneLogin = () => {
        setLoginError(false);
        setShowPhoneLogin(!showPhoneLogin);
        setCodeSent(false);
        setCode('');
    };

    const handleCancelPhoneLogin = () => {
        setLoginError(false);
        setShowPhoneLogin(false);
        setCodeSent(false);
        setPhoneNumber('');
        setCode('');
    };

    const toInternationalFormat = (number) => {
        //assumes Israel: a leading single 0 is the national trunk prefix, replace it with +972
        const trimmedNumber = number ? number.trim() : number;
        if (trimmedNumber && trimmedNumber.startsWith('0')) {
            return '+972' + trimmedNumber.slice(1);
        }
        return trimmedNumber;
    };

    const handleSendCode = async () => {
        setLoginError(false);
        setPhoneLoading(true);
        const internationalPhoneNumber = toInternationalFormat(phoneNumber);
        LogInfo("Requesting signing code for phone number: " + internationalPhoneNumber);
        const success = await GetSigninCode(internationalPhoneNumber, communicationMethod);
        setPhoneLoading(false);
        setCodeSent(success);
        setLoginError(!success);
    };

    const handleVerifyCode = async () => {
        setLoginError(false);
        setPhoneLoading(true);
        const internationalPhoneNumber = toInternationalFormat(phoneNumber);
        const trimmedCode = code.trim();
        LogInfo("Validating signing code for phone number: " + internationalPhoneNumber);
        const token = await ValidateSigninCode(internationalPhoneNumber, trimmedCode);
        if (token) {
            const details = jwtDecode(token);
            const loginResult = await loginWithPhone(details, internationalPhoneNumber);
            setPhoneLoading(false);
            setLoginError(!loginResult);
        } else {
            setPhoneLoading(false);
            setLoginError(true);
        }
    };

    return (
    <Fragment>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <List className='list-container'
                sx={{
                    pt: 0,
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                    minWidth: 400,
                    width: '400px',
                    marginBottom: 2,
                }}>
                <ListItem sx={{backgroundColor:"grey.200"}}>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <img src={c4ulogo} alt="Connect4U Logo" style={{height: "60px", width: "60px", p:2}}></img>
                        <Typography variant="h4" color="GrayText" sx={{p:2}}> Connect 4U</Typography>
                    </div>
                </ListItem>
                <ListItem sx={{backgroundColor:"grey.200"}}>
                <ListItemText sx={{textAlign:"center"}}>
                    <Typography variant='h5' color={'GrayText'}>{t('c4uguard')}</Typography></ListItemText>
                </ListItem>
                {loginError && 
                    <ListItem sx={{backgroundColor:"red", textAlign:"center"}}  >
                        <Typography  variant='h5' color={'black'}  sx={{ textAlign: 'center' }}>{t('loginError')}</Typography>
                    </ListItem>
                }
                {!codeSent &&
                    <ListItem >
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '100%', gap: '12px', direction: 'ltr' }}>
                            <GoogleLogin
                                    onSuccess={(credentialResponse) => {
                                        handleLogin(credentialResponse);}}
                                    onError={() => {console.log("Login Failed");
                                        setLoginError(true);}
                                    }
                            />
                            <Button variant="outlined" onClick={handleTogglePhoneLogin} sx={{height: '40px'}}>
                                {t('signInWithPhone')}
                            </Button>
                        </div>
                    </ListItem>
                }
                {showPhoneLogin &&
                    <ListItem>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '16px' }}>
                            {!codeSent ?
                                <Fragment>
                                    <TextField
                                        label={t('phoneNumber')}
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && phoneNumber && !phoneLoading) {
                                                handleSendCode();
                                            }
                                        }}
                                        disabled={phoneLoading}
                                        fullWidth
                                        autoFocus
                                    />
                                    <FormControl>
                                        <FormLabel>{t('receiveCodeVia')}</FormLabel>
                                        <RadioGroup
                                            row
                                            value={communicationMethod}
                                            onChange={(e) => setCommunicationMethod(e.target.value)}
                                        >
                                            <FormControlLabel value="sms" control={<Radio />} label={t('sms')} />
                                            <FormControlLabel value="email" control={<Radio />} label={t('email')} />
                                        </RadioGroup>
                                    </FormControl>
                                    <Button
                                        variant="contained"
                                        onClick={handleSendCode}
                                        disabled={!phoneNumber || phoneLoading}
                                    >
                                        {t('sendCode')}
                                    </Button>
                                </Fragment>
                                :
                                <Fragment>
                                    <TextField
                                        label={t('verificationCode')}
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && code && !phoneLoading) {
                                                handleVerifyCode();
                                            }
                                        }}
                                        disabled={phoneLoading}
                                        fullWidth
                                        autoFocus
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleVerifyCode}
                                        disabled={!code || phoneLoading}
                                    >
                                        {t('verifyCode')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        onClick={handleCancelPhoneLogin}
                                        disabled={phoneLoading}
                                    >
                                        {t('cancel')}
                                    </Button>
                                </Fragment>
                            }
                        </div>
                    </ListItem>
                }
            </List>
        </div>
        <Footer/>
        </Fragment>
    );
};

export default Login;